import { Skeleton } from "@/components/ui/skeleton";
import { LinesStackSkeleton } from "@/components/skeletons/shared";

export const brandGridClass =
    "grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 min-[1540px]:grid-cols-4";

function BrandCardSkeleton() {
    return (
        <div className="gs-brand-card gs-brand-card-skeleton">
            <div className="gs-brand-card__inner">
                <Skeleton className="gs-brand-card-skeleton-avatar" />
                <Skeleton className="gs-brand-card-skeleton-name" />
                <Skeleton className="gs-brand-card-skeleton-id" />
                <Skeleton className="gs-brand-card-skeleton-company" />
                <Skeleton className="gs-brand-card-skeleton-link" />
            </div>
        </div>
    );
}

function BrandListSkeleton({ rowCount = 3 }: { rowCount?: number }) {
    return (
        <div className={brandGridClass}>
            {Array.from({ length: rowCount }).map((_, index) => (
                <div key={index} id={index === 0 ? "brand-list-data-loader" : undefined}>
                    <BrandCardSkeleton />
                </div>
            ))}
        </div>
    );
}

function DashboardStatSkeleton() {
    return (
        <>
            {Array.from({ length: 4 }).map((_, index) => (
                <div className="gs-dashboard-stat-col" key={index}>
                    <div
                        className="gs-dashboard-stat gs-dashboard-stat-skeleton"
                        id={index === 0 ? "dashboard-stats-loader" : undefined}
                    >
                        <Skeleton className="h-6 w-12 rounded-[4px]" />
                        <Skeleton className="gs-dashboard-stat-skeleton-label h-4 w-20 rounded-[4px]" />
                    </div>
                </div>
            ))}
        </>
    );
}

function ProfilePageSkeleton() {
    return (
        <>
            <div className="gs-profile-fields">
                {Array.from({ length: 3 }).map((_, index) => (
                    <div className="gs-profile-row" key={index}>
                        <div className="gs-profile-col">
                            <div className="gs-profile-skeleton-label-slot">
                                <Skeleton className="h-4 w-24 rounded-[4px]" />
                            </div>
                            {index === 0 ? (
                                <div className="gs-profile-skeleton-field">
                                    <Skeleton className="gs-profile-skeleton-input rounded-[4px]" />
                                    <Skeleton className="gs-profile-skeleton-icon rounded-full" />
                                </div>
                            ) : (
                                <Skeleton className="gs-profile-skeleton-input rounded-[4px]" />
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="gs-profile-actions">
                <Skeleton className="gs-profile-skeleton-save rounded-[4px]" />
            </div>
        </>
    );
}

function ManageAiSkeleton({ rowCount = 4 }: { rowCount?: number }) {
    return (
        <LinesStackSkeleton
            rowCount={rowCount}
            wrapperClassName="gs-manage-ai-loader space-y-3"
            rowClassName="h-16 w-full rounded-md"
        />
    );
}

function AiConfigurationSkeleton({ rowCount = 10 }: { rowCount?: number }) {
    return (
        <div className="gs-ai-config-skeleton" aria-hidden>
            {Array.from({ length: rowCount }).map((_, index) => (
                <div key={index} className="gs-ai-config-skeleton-field">
                    <Skeleton className="gs-ai-config-skeleton-label h-4 w-40 max-w-full rounded-[4px]" />
                    <Skeleton className="gs-ai-config-skeleton-input h-[calc(2.125rem+2px)] w-full rounded-[4px]" />
                </div>
            ))}
            <div className="gs-ai-config-actions">
                <Skeleton className="h-[calc(2.125rem+2px)] w-40 rounded-[4px]" />
                <Skeleton className="h-[calc(2.125rem+2px)] w-24 rounded-[4px]" />
            </div>
        </div>
    );
}

function InstructionListSkeleton({ rowCount = 5 }: { rowCount?: number }) {
    return (
        <LinesStackSkeleton rowCount={rowCount} wrapperClassName="space-y-4 p-5" rowClassName="h-8 w-full" />
    );
}

function BrandDetailsSkeleton() {
    return (
        <div className="gs-brand-form-card gs-list-page-card gap-0 py-0 shadow-none" aria-hidden>
            <div className="gs-brand-form-inner">
                <Skeleton className="gs-brand-detail-skeleton-title mb-2 h-6 w-32 rounded-[4px]" />
                <Skeleton className="gs-brand-detail-skeleton-id mb-2 h-4 w-72 max-w-full rounded-[4px]" />

                <div className="gs-brand-form-head">
                    <Skeleton className="h-3 w-10 rounded-[4px]" />
                </div>
                <div className="gs-brand-form-group">
                    {Array.from({ length: 2 }).map((_, index) => (
                        <div className="gs-brand-form-item" key={index}>
                            <div className="gs-brand-form-col">
                                <div className="gs-brand-form-label">
                                    <Skeleton className="h-4 w-28 rounded-[4px]" />
                                </div>
                                <div className="gs-brand-form-value">
                                    <Skeleton className="gs-brand-form-input h-[calc(2.125rem+2px)] w-full max-w-[250px] rounded-[4px]" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="gs-brand-form-section">
                    <div className="gs-brand-form-head">
                        <Skeleton className="h-3 w-56 max-w-full rounded-[4px]" />
                    </div>
                    <div className="gs-brand-form-item">
                        <div className="gs-brand-form-col gs-brand-form-col--end gs-brand-form-col--align-start">
                            <div className="gs-brand-form-label">
                                <Skeleton className="h-4 w-56 max-w-full rounded-[4px]" />
                            </div>
                            <div className="gs-brand-form-value flex-1 gs-brand-form-value--brand-preview">
                                <div className="gs-brand-detail-customize-value">
                                    <div className="gs-brand-detail-swatches">
                                        {Array.from({ length: 4 }).map((_, index) => (
                                            <Skeleton key={index} className="gs-brand-detail-skeleton-swatch" />
                                        ))}
                                    </div>
                                    <div className="gs-brand-detail-preview">
                                        <Skeleton className="gs-brand-detail-skeleton-preview" />
                                        <Skeleton className="gs-brand-detail-skeleton-preview" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-6 border-t-[1px] pt-4 border-border">
                    <Skeleton className="h-[calc(2.125rem+2px)] w-24 rounded-[4px]" />
                </div>
            </div>
        </div>
    );
}

export {
    AiConfigurationSkeleton,
    BrandCardSkeleton,
    BrandDetailsSkeleton,
    BrandListSkeleton,
    DashboardStatSkeleton,
    InstructionListSkeleton,
    ManageAiSkeleton,
    ProfilePageSkeleton,
};
